/**
 * Creates a complete URL by adding dynamic parameters to a base URL.
 *
 * @param {string} baseURL - The starting URL (e.g., "https://example.com/search").
 * @param {Object} params - An object containing key-value pairs for the GET parameters.
 * @returns {string} The final URL with the appended, encoded query string.
 */
export function createURLWithParams(baseURL, params) {

  // 1. Create a URLSearchParams object from the parameters object
  // This handles encoding keys and values (e.g., spaces become + or %20)
  const urlParams = new URLSearchParams(params).toString();

  // 2. Check if the baseURL already contains a query string
  // This determines whether to use '?' or '&' to append the new params
  const separator = baseURL.includes('?') ? '&' : '?';

  // 3. Combine the base URL, separator, and the new query string
  return removeDuplicateQueryParams(`${baseURL}${separator}${urlParams}`);
}

/**
 * Removes duplicate query parameters from a URL, keeping only the first instance of each key.
 *
 * @param {string} urlString - The full URL string to process.
 * @returns {string} The URL string with duplicate parameters removed.
 */
function removeDuplicateQueryParams(urlString) {
  // 1. Create a URL object from the string.
  const url = new URL(urlString);

  // 2. Get the current URLSearchParams object.
  const params = url.searchParams;

  // 3. Create a new, empty URLSearchParams object for the cleaned list.
  const newParams = new URLSearchParams();

  // 4. Use a Set to track parameter keys that have already been added.
  const seenKeys = new Set();

  // 5. Iterate through the original parameters.
  //    The URLSearchParams.forEach() method is used here.
  params.forEach((value, key) => {
    // Check if the key has ALREADY been seen.
    if (!seenKeys.has(key)) {
      // If it's the first time seeing this key, add it to the new parameter list.
      newParams.append(key, value);

      // Mark the key as seen.
      seenKeys.add(key);
    }
    // If the key HAS been seen, we do nothing (effectively removing the duplicate).
  });

  // 6. Replace the original search string with the new, cleaned search string.
  url.search = newParams.toString();

  // 7. Return the full, cleaned URL as a string.
  return url.toString();
}

/**
 * Checks if a URL already has a specific GET parameter.
 * If the parameter is not present, it adds it with the given key and value.
 *
 * @param {string} url The original URL string.
 * @param {string} key The key of the query parameter to check/add.
 * @param {string} value The value to use if the parameter is added.
 * @returns {string} The updated URL string.
 */
export function maybeAddQueryParam(url, key, value) {
  // Use URLSearchParams to easily check for existing keys
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    // 1. Check if the key already exists
    if (params.has(key)) {
      // Key found, return the original URL
      return url;
    }

    // 2. Key not found, add the new parameter
    params.set(key, value);

    // 3. Reconstruct and return the full URL
    // The `toString()` method on URL automatically includes the updated searchParams.
    return urlObj.toString();

  } catch (e) {
    // Handle potential errors, e.g., if the URL is not a valid absolute URL
    console.error("Invalid URL provided to maybeAddQueryParam:", url, e);
    // Fallback: simply append the parameter as a string (less robust for existing params)
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${key}=${encodeURIComponent(value)}`;
  }
}
